import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

function AnimatedCounter({ value, suffix, isYear = false }: { value: number; suffix: string; isYear?: boolean }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const triggered = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !triggered.current) {
          triggered.current = true;
          const duration = 2000;
          const start = performance.now();
          const tick = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(isYear ? value : Math.floor(eased * value));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value, isYear]);

  return (
    <div ref={ref} className="font-heading font-bold text-3xl md:text-4xl text-white tabular-nums">
      {isYear ? value.toString() : count.toLocaleString('es-BO')}{suffix}
    </div>
  );
}

export default function HeroSection() {
  return (
    <section className="relative min-h-[600px] md:min-h-[720px] flex items-center justify-center overflow-hidden bg-foreground-950">
      {/* Background image - Represa San Jacinto */}
      <div className="absolute inset-0">
        <img
          src="https://storage.readdy-site.link/project_files/418b164c-f08c-4ba4-9398-9b68655eb361/779129b8-ff21-447a-97d6-ecb8acc91660_compressed_image.webp"
          alt="Represa San Jacinto - Tarija Bolivia"
          className="w-full h-full object-cover"
        />
        {/* Overlays for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" />
      </div>

      {/* Decorative subtle shapes */}
      <div className="absolute top-1/4 left-10 w-64 h-64 bg-accent-400/10 rounded-full blur-3xl animate-blob" />
      <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-primary-400/10 rounded-full blur-3xl animate-blob" style={{ animationDelay: '-4s' }} />

      {/* Content */}
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6 animate-fade-in">
            <i className="ri-shield-check-line text-accent-300 text-sm w-4 h-4 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/90">Personería Jurídica desde 2011 — Servicio de agua desde 2015</span>
          </div>

          {/* Title */}
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-tight mb-6">
            Agua
            <br />
            <span className="text-accent-300">Para Tu Familia</span>
          </h1>

          {/* Description */}
          <p className="text-base md:text-lg text-white/75 max-w-2xl mx-auto mb-8 leading-relaxed">
            CODESAJAN distribuye agua en San Jacinto Norte Zona Oeste, Tarija.
            Gestión comunitaria transparente al servicio de nuestras familias.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <a
              href="https://codesajancuenta.fluxbostudio.com"
              className="px-7 py-3.5 bg-accent-500 hover:bg-accent-600 text-white font-semibold rounded-lg transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap text-sm"
            >
              <span className="flex items-center gap-2">
                <i className="ri-user-settings-line w-4 h-4 flex items-center justify-center"></i>
                Mi Cuenta
              </span>
            </a>
            <Link
              to="/pagar"
              className="px-7 py-3.5 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg border border-white/25 transition-all duration-300 hover:-translate-y-0.5 backdrop-blur-sm whitespace-nowrap text-sm"
            >
              <span className="flex items-center gap-2">
                <i className="ri-qr-code-line w-4 h-4 flex items-center justify-center"></i>
                Pagar por QR
              </span>
            </Link>
          </div>
        </div>

        {/* Floating stats bar */}
        <div className="max-w-4xl mx-auto mt-14 md:mt-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { value: +70, suffix: '', label: 'Familias Conectadas' },
              { value: 3, suffix: ' km', label: 'Red de Tuberías' },
              { value: 2015, suffix: '', label: 'Inicio del Servicio', isYear: true },
              { value: 100, suffix: '%', label: 'Pago Virtual' },
            ].map((stat, i) => (
              <div
                key={stat.label}
                className="bg-white/10 backdrop-blur-md rounded-xl border border-white/15 p-4 md:p-5 text-center animate-slide-up"
                style={{ animationDelay: `${0.15 * i}s` }}
              >
                <AnimatedCounter value={stat.value} suffix={stat.suffix} isYear={stat.isYear} />
                <div className="text-xs md:text-sm text-white/60 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}